import React, { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { makeStyles } from '@material-ui/core'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { useHistory } from 'react-router-dom'

import InvoiceFormSidePanel from './InvoiceFormSidePanel'
import InvoiceTotalsInputs from './InvoiceTotalsInputs'
import {
  useFetchInvoiceQuery,
  useFetchLineItemsQuery,
  useEditInvoiceMutation,
} from '../api'
import {
  Button,
  Container,
  fileToBase64,
  Form,
  FormikFileField,
  FIELD_REQUIRED,
  InvoiceOrders,
  PageTitle,
  PDFPreview,
  Typography,
} from 'src/modules/ui'

const DATE_FORMAT = 'YYYY-MM-DD'

const useStyles = makeStyles(theme => ({
  body: {
    backgroundColor: theme.palette.fadedPurple.main,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    [theme.breakpoints.up('desktop')]: {
      flexDirection: 'row',
    },
  },
  content: {
    flexGrow: 1,
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 0,
    },
  },
  midInput: {
    '& .MuiFormControl-root': {
      minWidth: '100%',
    },
    [theme.breakpoints.up('desktop')]: {
      '& .MuiFormControl-root': {
        minWidth: 160,
      },
    },
  },
}))

const EditInvoice = ({
  id,
  creditNote,
  creditNoteDocument,
  deliveryCharge,
  discount,
  document,
  invoiceDate,
  invoiceDueDate,
  invoiceNumber,
  orders,
  otherCharge,
  notes,
}) => {
  const history = useHistory()
  const [editInvoice] = useEditInvoiceMutation()

  const orderIds = orders.map(({ id }) => id)
  const lineItemsQuery = useFetchLineItemsQuery(
    { orderId: orderIds },
    {
      refetchOnMountOrArgChange: true,
    }
  )

  if (lineItemsQuery.isLoading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    )
  }

  const initialValues = {
    creditNote,
    creditNoteDocument,
    creditNoteDocumentData: null,
    deliveryCharge,
    discount,
    document,
    documentData: null,
    invoiceDate: invoiceDate ? moment(invoiceDate, DATE_FORMAT) : null,
    invoiceDueDate: invoiceDueDate ? moment(invoiceDueDate, DATE_FORMAT) : null,
    invoiceNumber,
    otherCharge,
    notes,
  }

  const validationSchema = Yup.object().shape({
    creditNote: Yup.number().typeError('Invalid number').nullable(),
    deliveryCharge: Yup.number().typeError('Invalid number').nullable(),
    discount: Yup.number().typeError('Invalid number').nullable(),
    invoiceNumber: Yup.string().required(FIELD_REQUIRED),
    otherCharge: Yup.number().typeError('Invalid number').nullable(),
    invoiceDate: Yup.date().typeError('Invalid date').required(FIELD_REQUIRED),
    invoiceDueDate: Yup.date().typeError('Invalid date').nullable(),
  })

  const handleSubmit = async (
    {
      buyerId,
      creditNote,
      creditNoteDocument,
      creditNoteDocumentData,
      deliveryCharge,
      discount,
      document,
      documentData,
      invoiceDate,
      invoiceDueDate,
      invoiceNumber,
      orderIds,
      otherCharge,
      notes,
    },
    { setErrors }
  ) => {
    try {
      await editInvoice({
        id,
        buyerId,
        creditNote,
        ...(!creditNoteDocument ? { creditNoteDocument: null } : {}),
        ...(creditNoteDocumentData ? { creditNoteDocumentData } : {}),
        deliveryCharge,
        discount,
        ...(!document ? { document: null } : {}),
        ...(documentData ? { documentData } : {}),
        invoiceDate: invoiceDate ? invoiceDate.toISOString() : null,
        invoiceDueDate: invoiceDueDate ? invoiceDueDate.toISOString() : null,
        invoiceNumber,
        orderIds,
        otherCharge,
        notes,
      }).unwrap()
      history.push(`/invoices/${id}`)
    } catch (err) {
      setErrors(err.data)
    }
  }

  return (
    <Container>
      <PageTitle>Edit Invoice</PageTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, handleSubmit, setFieldValue, values }) => (
          <InnerForm
            {...{
              isSubmitting,
              handleSubmit,
              setFieldValue,
              values,
              orders,
            }}
            lineItems={lineItemsQuery.data}
          />
        )}
      </Formik>
    </Container>
  )
}

const InnerForm = ({
  isSubmitting,
  handleSubmit,
  setFieldValue,
  values,
  orders,
  lineItems,
}) => {
  const classes = useStyles()
  const [hidePDF, setHidePDF] = useState(false)
  const togglePDF = () => {
    setHidePDF(!hidePDF)
  }

  const prepareDocument = useCallback(async () => {
    const data = await fileToBase64(values.document)
    setFieldValue('documentData', data)
  }, [setFieldValue, values.document])

  useEffect(() => {
    if (values.document && typeof values.document !== 'string') {
      prepareDocument()
    } else {
      setFieldValue('documentData', null)
    }
  }, [values.document, prepareDocument, setFieldValue])

  const prepareCreditNoteDocument = useCallback(async () => {
    const data = await fileToBase64(values.creditNoteDocument)
    setFieldValue('creditNoteDocumentData', data)
  }, [setFieldValue, values.creditNoteDocument])

  useEffect(() => {
    if (
      values.creditNoteDocument &&
      typeof values.creditNoteDocument !== 'string'
    ) {
      prepareCreditNoteDocument()
    } else {
      setFieldValue('creditNoteDocumentData', null)
    }
  }, [values.creditNoteDocument, prepareCreditNoteDocument, setFieldValue])

  return (
    <Form className={classes.body}>
      <div className={classes.content}>
        <div className={classes.inputRow}>
          <div className={classes.midInput}>
            <FormikFileField
              label="Invoice PDF"
              margin="normal"
              name="document"
            />
          </div>
          {values.document && (
            <>
              {!hidePDF && (
                <Button color="secondary" size="large" onClick={togglePDF}>
                  Hide PDF
                </Button>
              )}
              {hidePDF && (
                <Button color="secondary" size="large" onClick={togglePDF}>
                  Show PDF
                </Button>
              )}
            </>
          )}
        </div>
        {!hidePDF && <PDFPreview document={values.document} />}
        <InvoiceOrders orders={orders} lineItems={lineItems} />
        <InvoiceTotalsInputs orders={orders} />
      </div>
      <InvoiceFormSidePanel>
        <Button
          color="primary"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          size="large"
          type="submit"
        >
          Save Invoice
        </Button>
      </InvoiceFormSidePanel>
    </Form>
  )
}

const EditInvoiceContainer = ({ match }) => {
  const { id } = match.params

  const invoiceQuery = useFetchInvoiceQuery(id, {
    refetchOnMountOrArgChange: true,
  })

  if (invoiceQuery.isLoading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    )
  }

  return <EditInvoice {...invoiceQuery.data} />
}

export default EditInvoiceContainer
